import topDomain from '@segment/top-domain';
import cookies from 'js-cookie';
import { analytics } from '../../helpers/analytics';
import { AnalyticsParams, PreferenceConfiguration, Preferences, PreferencesCategory } from './types';
import config from '../../common/config';
import { log, warn } from '../../helpers/gtm/logger';

const DEFAULT_COOKIE_NAME = 'tracking-preferences';
const COOKIE_DEFAULT_EXPIRES = 365;

export const ACCEPT_ALL_PREFERENCES: Preferences = Object.values(PreferencesCategory).reduce((acc, category) => {
	acc[category] = true;
	return acc;
}, {} as Preferences);

// GDPR / Quebec Law 25 / ePrivacy: non-essential categories must default to OFF
export const DEFAULT_PREFERENCES: Preferences = Object.values(PreferencesCategory).reduce((acc, category) => {
	acc[category] = category === PreferencesCategory.FUNCTIONAL;
	return acc;
}, {} as Preferences);

export const DEFAULT_PREFERENCES_CONFIG: PreferenceConfiguration = {
	[PreferencesCategory.FUNCTIONAL]: {
		title: 'Essential',
		description: 'Required for the site to function. These handle your preferences, sessions, and consent settings.'
	},
	[PreferencesCategory.ANALYTICS]: {
		title: 'Analytics',
		description: 'Help us understand how visitors use our site so we can improve the experience. This includes usage data and site traffic analysis.'
	},
	[PreferencesCategory.ADVERTISING]: {
		title: 'Advertising',
		description: 'Used to measure ad effectiveness and deliver relevant ads. Advertising partners may use this data to build a profile of your interests.'
	}
};

export function loadPreferences(cookieName?: string): Preferences | undefined {
	const cookieContent = cookies.get(cookieName || DEFAULT_COOKIE_NAME);
	if (!cookieContent) {
		return undefined;
	}
	try {
		return JSON.parse(cookieContent);
	}
 	catch {
		warn('[Consent] Failed to parse tracking-preferences cookie — treating as no saved preferences');
		return undefined;
	}
}
export interface SavePreferences {
	preferences: Preferences;
	cookieDomain?: string;
	cookieName?: string;
	cookieExpires?: number;
}

export function savePreferences({
	preferences,
	cookieDomain,
	cookieName,
	cookieExpires
}: SavePreferences) {
	const domain = cookieDomain || topDomain(window.location.href);
	const expires = cookieExpires || COOKIE_DEFAULT_EXPIRES;

	cookies.set(cookieName || DEFAULT_COOKIE_NAME, JSON.stringify(preferences), {
		expires,
		domain
	});
}

/**
 * Check if current environment is a demo/testing environment
 * This function is tree-shaken in production builds
 */
function isDemoEnv(): boolean {
	// Non-production environment detection
	// Tree-shaken in production builds via Webpack DefinePlugin
	if (process.env.NODE_ENV !== 'production') {
		return ['demo.tablz.com', 'demo-staging.tablz.com', 'localhost:8080'].includes(location.host);
	}
	return false;
}

export function conditionallyLoadAnalytics({
	preferences
}: AnalyticsParams) {

	if (config.analytics.enable === false || isDemoEnv()) {
		return;
	}

	log('[Consent] Legacy analytics loading with preferences:', preferences ?? 'none (accept all)');

	// Load a.js normally when consent isn't required and there's no preferences
	analytics.load(preferences);
	return;
}